import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePriceGroup } from 'core/contexts/priceGroup';
import { EntityService } from 'core/api/services/lookUp';
import { Field, Form } from 'react-final-form';
import { BookingOrigin } from '@utriper/core';
import { CharacterCounter, LookUpExperience, SelectCheckbox, TextInput } from '@utriper/core-ui';
import { Box, FormHelperText, Grid2, InputLabel } from '@mui/material';
import { styled } from '@mui/material/styles';
import Title from '../../../components/Title';
import ActionButtons from '../ActionButtons';

const Container = ({ children, ...props }) => {
    return (
        <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="4px" {...props}>
            {children}
        </Grid2>
    );
};

const Label = styled(InputLabel)({
    fontSize: '18px',
    fontWeight: 400,
    color: '#131313',
});

const PriceGroupDataForm = () => {
    const { i18n, t } = useTranslation();
    const translate = (key) => t(`price_group_wizard.price_group_data_form.${key}`);
    const history = useHistory();
    const { priceGroupTabId, priceGroupData, handleUpdatePriceGroupData, handleNext, resetWizard } = usePriceGroup();
    const [submitted, setSubmitted] = useState(false);

    const validateForm = (values) => {
        const errors = {};
        if (!values.name) {
            errors.name = translate('validate_form.required');
        }
        if (values?.name?.length > 255) {
            errors.name = translate('validate_form.length');
        }
        if (!values?.experienceId) {
            errors.experienceId = translate('validate_form.required');
        }
        if (!Array.isArray(values?.originIds) || values.originIds?.length <= 0) {
            errors.originIds = translate('validate_form.required');
        }

        return errors;
    };

    const comingSoonLabel = t('soon');
    const originOptions = useMemo(
        () =>
            BookingOrigin.getValidOrigins().map((bkOrigin) => {
                const disabled = BookingOrigin.isOTABooking(bkOrigin);
                const name = BookingOrigin.getTranslatedName(BookingOrigin.types, bkOrigin, i18n.language);

                return {
                    id: bkOrigin,
                    name: disabled ? `${name} (${comingSoonLabel})` : name,
                    disabled,
                };
            }),
        [i18n.language, comingSoonLabel]
    );

    const handleSubmit = (values) => {
        handleUpdatePriceGroupData(values);
        handleNext();
    };

    const handleBack = (event) => {
        event?.preventDefault();
        resetWizard();
        history.push(`/price-list/${priceGroupTabId}`);
    };

    return (
        <Form
            onSubmit={handleSubmit}
            initialValues={priceGroupData}
            validate={validateForm}
            render={({ values, errors, handleSubmit, form }) => {
                const handleChangeExperience = (newValue) => {
                    form.change('experienceId', newValue);
                };
                const handleChangeOrigins = (newValues) => {
                    form.change('originIds', [...newValues]);
                };

                return (
                    <form onSubmit={handleSubmit} noValidate style={{ width: '100%', maxWidth: '480px' }}>
                        <Box component="section">
                            <Title title={translate('title')} description={translate('description')} />

                            {/* Form */}
                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                                {/* Name */}
                                <Container>
                                    <Label>{translate('form.name')}</Label>
                                    <Field
                                        name="name"
                                        component={TextInput}
                                        placeholder={translate('form.name_placeholder')}
                                        slotProps={{
                                            input: {
                                                inputProps: {
                                                    maxLength: 255,
                                                },
                                            },
                                        }}
                                    />
                                </Container>

                                {/* Description */}
                                <Container>
                                    <Label>{translate('form.description')}</Label>
                                    <Field
                                        name="description"
                                        component={TextInput}
                                        placeholder={translate('form.description_placeholder')}
                                        multiline
                                        minRows={2}
                                        slotProps={{
                                            input: {
                                                sx: {
                                                    '& textarea': {
                                                        resize: 'vertical',
                                                        minHeight: '32px',
                                                        maxHeight: '160px',
                                                        overflow: 'auto',
                                                    },
                                                },
                                                inputProps: {
                                                    maxLength: 255,
                                                },
                                            },
                                        }}
                                    />
                                    <CharacterCounter length={values?.description?.length} />
                                </Container>

                                {/* Experience */}
                                <Container
                                    sx={{
                                        '& .MuiAutocomplete-root': { width: '100%' },
                                        '& .MuiOutlinedInput-notchedOutline': submitted && errors?.experienceId ? { borderColor: 'red' } : '',
                                    }}
                                >
                                    <Label>{translate('form.experience')}</Label>
                                    <Field name="experienceId" render={({ input }) => <LookUpExperience {...input} label={translate('form.experience_label')} entityService={EntityService} value={values?.experienceId} initialValue={values?.experienceId} onChange={handleChangeExperience} />} />
                                    {submitted && errors?.experienceId && <FormHelperText error={submitted && errors?.experienceId}>{errors.experienceId}</FormHelperText>}
                                </Container>

                                {/* Origins */}
                                <Container>
                                    <Label>{translate('form.origins')}</Label>
                                    <Field name="originIds" component={SelectCheckbox} options={originOptions} values={values?.originIds ?? []} label={translate('form.origins_label')} onChange={handleChangeOrigins} hasError={submitted && errors?.originIds} />
                                    {submitted && errors?.originIds && <FormHelperText error={submitted && errors?.originIds}>{errors.originIds}</FormHelperText>}
                                </Container>
                            </Grid2>

                            <ActionButtons
                                handleSubmit={(e) => {
                                    setSubmitted(true);
                                    return handleSubmit(e);
                                }}
                                handleBack={handleBack}
                            />
                        </Box>
                    </form>
                );
            }}
        />
    );
};

export default PriceGroupDataForm;
